// shared-lib/cabinet-runs/vanityRunAccessories.js
//
// Phase 2 of the bathroom-cabinet-runs plan: a vanity-profile run's
// ACCESSORIES — the mirror over the basin and the light over the mirror —
// follow the run.
//
// THE OWNERSHIP SPLIT IS THE WHOLE DESIGN, and it is the kitchen's, one
// vocabulary over:
//
//   the RUN owns   cabinet bodies, fillers, corner units, the countertop,
//                  and (drawn / generated) the basin and faucet. They carry
//                  `runId`; a re-solve sweeps and re-emits them.
//   the GROUP owns the mirror, the sconces, the overhead or ceiling light.
//                  They carry `fixtureGroupId` and NEVER a `runId`, so
//                  `mergeResolvedRuns`' keep rule leaves them alone — which
//                  is exactly what makes a user's swapped mirror survive
//                  every re-solve, the same rule the kitchen's
//                  retained-but-never-re-emitted cabinetry follows.
//
// So this module is not part of the fill. It runs AFTER the merge, reads what
// the run now looks like, and does two things per sink group:
//
//   1. MOVES what is already there, through the one pass the cloud resize
//      path uses (`vanity-assembly/repositionAccessories`, promoted out of
//      `functions/` for this). A bay drag is the same question a cabinet
//      resize asks: the body moved along its wall, take its accessories with
//      it, narrow one that no longer fits, and drop one that cannot be placed
//      at all.
//   2. TOPS UP what is missing — and only that. The contract is
//      `assembleStandaloneSinkAccessories`': each accessory is skipped when
//      the group already has one, so calling this on every re-solve is
//      idempotent and a user's own mirror is never rebuilt.
//
// The mirror and light are PROCEDURAL here (user ruling 2026-09-25, the
// plan's open decision #2). A catalog pick needs a Firestore query, and the
// re-solve this hangs off runs inside a drag. A caller that HAS a catalog
// record may pass it as `overheadLightModel`.
//
// NOTHING UPGRADES THEM LATER, today. `optimize_vanity` builds a mirror only
// when the group has none, so an existing procedural one is repositioned and
// never swapped for a product — a drawn vanity's mirror stays procedural
// until someone writes an explicit upgrade pass.
//
// Pure: no I/O, no mutation of the input objects.

const { profileOf, VANITY_PROFILE_ID } = require('./profiles');
const { runAnswersTo } = require('./runIds');
const {
  repositionAccessoriesToCabinet,
} = require('../vanity-assembly/repositionAccessories');
const {
  buildProceduralMirror,
} = require('../vanity-assembly/buildProceduralMirror');
const {
  buildOverheadLightObject,
} = require('../vanity-assembly/createAccessories');
const { planVanityMirrors } = require('../vanity-assembly/planVanityMirrors');
const {
  calculateMirrorWallSpace,
} = require('../vanity-assembly/calculateMirrorWallSpace');
const { makeWallProjector } = require('../vanity-assembly/placementUtils');
const { overheadBarWidth } = require('../vanity-assembly/accessoryRules');
const { VANITY_DIMENSIONS } = require('../vanity-assembly/constants');
const {
  getFixtureGroupMirrors,
  getFixtureGroupOverheadLights,
  getFixtureGroupCeilingLights,
  computeCountertopTopY,
  isSconce,
} = require('../vanity-assembly/fixtureGroup');
const {
  isSinkLike,
  getKitchenSinkAnchor,
  findKitchenCountertopFor,
} = require('../kitchen-assembly/fixtureGroup');
const {
  attachKitchenSinkLinksForGroups,
} = require('../kitchen-assembly/attachKitchenSinkLinks');
const { pruneDanglingGroupLinks } = require('../group-customization/roomLinks');

/** The run's own basins: a sink it owns, never the base cabinet under one. */
function runBasins(objects, run) {
  return objects.filter(
    (o) => o?.runId && runAnswersTo(run, o.runId) && isSinkLike(o)
  );
}

/**
 * The group's accessories, split the way `repositionAccessoriesToCabinet`
 * takes them. `wallLights` is sconces AND the overhead bar — that pass
 * dispatches between them itself (`isOverheadLight`), and handing the
 * overhead in as a `vanityLight` instead would track it to the nearest mirror
 * by offset rather than pin it to the mirror's own centre.
 */
function groupAccessories(objects, groupId) {
  const mirrors = getFixtureGroupMirrors(objects, groupId);
  const overheads = getFixtureGroupOverheadLights(objects, groupId);
  const sconces = objects.filter(
    (o) => o?.fixtureGroupId === groupId && isSconce(o)
  );
  const ceiling = getFixtureGroupCeilingLights(objects, groupId);
  return { mirrors, wallLights: [...overheads, ...sconces], ceiling };
}

/**
 * The BODY the accessories hang over: the basin's own base cabinet.
 *
 * Read from `parents`, not from a field on the basin. `parents` is the
 * anchoring record — "compute my position from this object" — and it is what
 * the builders write; a `sinkCabinetId` is handed to `buildVanitySinkBasin`
 * as an argument and never survives onto the object. The group anchor is the
 * fallback, which is the same preference order every other sink-installation
 * reader uses.
 */
function sinkBody(objects, basin, groupId) {
  const parent = (basin?.parents || []).find(
    (p) => p?.type === 'lower-cabinet'
  );
  if (parent) {
    const byParent = objects.find((o) => o?.id === parent.id);
    if (byParent) return byParent;
  }
  return getKitchenSinkAnchor(
    objects.filter((o) => o?.fixtureGroupId === groupId)
  );
}

/** The wall an accessory hangs on: the body's own, resolved against the room. */
function hostWall(room, body) {
  const wallId = (body?.parentWallIds || [])[0];
  if (!wallId) return null;
  return (room?.walls || []).find((w) => w?.id === wallId) || null;
}

/**
 * Move one group's existing accessories to follow the basins' new position.
 *
 * The shift is measured from a BASIN, not from the cabinet: a bay's cabinet
 * id is content-keyed on its span (`emitBay`), so a re-solve that slides the
 * bay by a millimetre mints a new id and a prior-position lookup finds
 * nothing, while `sink_<bayId>` is stable and is REUSED across solves by
 * design (`fillRun`'s `reuse()`).
 */
function repositionGroup({
  objects,
  groupId,
  basins,
  priorById,
  cabinet,
  wall,
  projector,
  room,
}) {
  const { mirrors, wallLights, ceiling } = groupAccessories(objects, groupId);
  if (!mirrors.length && !wallLights.length && !ceiling.length) {
    return { objects, removedIds: [] };
  }
  const priorBasins = basins
    .map((b) => priorById.get(b.id))
    .filter((b) => b?.center);
  if (!priorBasins.length) return { objects, removedIds: [] };

  const anchorNew = basins.find((b) => priorById.has(b.id));
  const anchorOld = anchorNew ? priorById.get(anchorNew.id) : null;
  if (!anchorNew || !anchorOld) return { objects, removedIds: [] };
  const centerShift =
    projector.projectOntoWall(anchorNew.center) -
    projector.projectOntoWall(anchorOld.center);
  const depthFromWall = (p) =>
    (p.x - projector.wallStart.x) * projector.wallNormal.x +
    (p.z - projector.wallStart.z) * projector.wallNormal.z;
  const depthCenterShift =
    depthFromWall(anchorNew.center) - depthFromWall(anchorOld.center);
  // Nothing moved — a bay edit elsewhere in the run, an appearance change.
  // Returning early is not just an optimisation: the reposition pass
  // collision-checks every candidate, and re-committing an accessory that
  // already sits where it sits would let a room that changed around it
  // (a wall drag, a new linen tower) cull an accessory this edit never
  // touched.
  if (Math.abs(centerShift) < 1e-6 && Math.abs(depthCenterShift) < 1e-6) {
    return { objects, removedIds: [] };
  }

  const byId = new Map(objects.filter((o) => o?.id).map((o) => [o.id, o]));
  const { updates, removeIds } = repositionAccessoriesToCabinet({
    mirrors,
    wallLights,
    vanityLights: ceiling,
    sinks: priorBasins,
    centerShift,
    depthCenterShift,
    isDoubleSink: basins.length >= 2,
    parentWall: wall,
    roomData: room,
    wallDir: projector.wallDir,
    wallNormal: projector.wallNormal,
    wallStart: projector.wallStart,
    projectOntoWall: projector.projectOntoWall,
    // The synthetic `<groupId>_vanity` key is how that pass asks for the
    // BODY's new centre (the cloud caller answers it the same way); without
    // it the double-sink branch compares a mirror's projection against its
    // own and hands both mirrors to the same basin.
    getUpdatedCenter: (id) =>
      id === `${groupId}_vanity` ? cabinet?.center : byId.get(id)?.center,
  });
  if (!updates.size && !removeIds.length) return { objects, removedIds: [] };
  const removed = new Set(removeIds);
  const next = objects
    .filter((o) => !removed.has(o?.id))
    .map((o) => (o?.id && updates.has(o.id) ? updates.get(o.id) : o));
  return { objects: next, removedIds: removeIds };
}

/**
 * Build the accessories one group is missing: a mirror per basin, then a
 * light bar over each mirror.
 */
function topUpGroup({
  objects,
  groupId,
  basins,
  cabinet,
  wall,
  projector,
  room,
  roomId,
  addedBy,
  overheadLightModel,
}) {
  const created = [];
  const cabinetWidth = cabinet?.dimensions?.x;
  if (!Number.isFinite(cabinetWidth) || !cabinet?.center) {
    return { objects, created };
  }
  const countertop = findKitchenCountertopFor(objects, basins[0]);
  const countertopTopY = computeCountertopTopY(countertop, basins[0]);
  if (countertopTopY == null) return { objects, created };

  let next = objects;
  let mirrors = getFixtureGroupMirrors(next, groupId);
  if (!mirrors.length) {
    const wallSpace = calculateMirrorWallSpace(
      cabinet.center,
      cabinetWidth,
      countertopTopY,
      wall,
      room
    );
    // ONE decision point for mirror count, placement and width, shared with
    // the scan and chat paths (`planVanityMirrors`) — a double-basin run gets
    // two mirrors for the same reason a double vanity does.
    const placements = planVanityMirrors({
      cabinetWidth,
      cabinetCenter: cabinet.center,
      sinks: basins,
      projectOntoWall: projector.projectOntoWall,
      maxWallSpaceWidth: wallSpace?.availableWidth,
    });
    const built = placements.map((placement, index) =>
      buildProceduralMirror({
        roomId,
        cabinetId: cabinet.id,
        cabinetCenter: {
          x: placement.lateralCenter?.x ?? cabinet.center.x,
          y: cabinet.center.y,
          z: placement.lateralCenter?.z ?? cabinet.center.z,
        },
        rotation: cabinet.rotation,
        parentWall: wall,
        parentWallIds: [wall.id],
        countertopTopY,
        fixtureGroupId: groupId,
        index,
        totalMirrors: placements.length,
        maxWidth: placement.width,
        maxHeight: wallSpace?.availableHeight ?? null,
        addedBy,
      })
    );
    created.push(...built);
    next = [...next, ...built];
    mirrors = built;
  }

  const haveLights = getFixtureGroupOverheadLights(next, groupId);
  if (mirrors.length && !haveLights.length) {
    const lights = mirrors.map((mirror, index) => {
      const barWidth = overheadBarWidth(mirror.dimensions.x);
      const light = buildOverheadLightObject({
        id: `overhead_light_${Date.now()}_${roomId}_${cabinet.id}_${index}`,
        mirrorCenter: mirror.center,
        mirrorHeight: mirror.dimensions.y,
        mirrorWidth: mirror.dimensions.x,
        // A task light belongs over the BASIN, which on a run is not the
        // mirror's centre whenever the bay put the bowl off-centre.
        lateralAnchor: (basins[index] || basins[0]).center,
        projectOntoWall: projector.projectOntoWall,
        lightDimensions: {
          x: barWidth,
          y: VANITY_DIMENSIONS.lightBar.height,
          z: VANITY_DIMENSIONS.lightBar.depth,
        },
        rotation: cabinet.rotation,
        fixtureGroupId: groupId,
        parentWallId: wall.id,
        modelData: overheadLightModel
          ? {
              id: overheadLightModel.modelId,
              url: overheadLightModel.modelUrl,
              name: overheadLightModel.modelName,
              scaleFactor: overheadLightModel.scaleFactor,
            }
          : null,
        parentWall: wall,
      });
      light.addedBy = addedBy;
      light.lastModified = new Date().toISOString();
      return light;
    });
    created.push(...lights);
    next = [...next, ...lights];
  }

  return { objects: next, created };
}

/**
 * The room's objects after a vanity-profile run has re-solved, with its
 * group-owned accessories planned and moved.
 *
 * @param {Object}   options
 * @param {Object}   options.room          the room AFTER the run merge —
 *   `objects` are the merged ones, `walls` the room's own walls.
 * @param {Object}   options.run           the resolved run. A kitchen-profile
 *   run returns the input untouched, by identity.
 * @param {Object[]} [options.priorObjects] the room's objects BEFORE the
 *   solve. Without them nothing is MOVED (there is no previous position to
 *   measure from); the top-up still runs, which is the draw-commit case.
 * @param {string}   [options.roomId]
 * @param {string}   [options.addedBy='manual-cabinet-run']
 * @param {Object}   [options.overheadLightModel] catalog record for the light
 *   bar; omitted → procedural.
 * @returns {{objects: Object[], created: Object[], removedIds: string[]}}
 *   `objects` is the input array by IDENTITY when nothing changed, so a
 *   caller can skip a write.
 */
function planVanityRunAccessories({
  room,
  run,
  priorObjects = [],
  roomId,
  addedBy = 'manual-cabinet-run',
  overheadLightModel = null,
}) {
  const objects = room?.objects || [];
  const unchanged = { objects, created: [], removedIds: [] };
  if (!run || profileOf(run).id !== VANITY_PROFILE_ID) return unchanged;

  const basins = runBasins(objects, run);
  if (!basins.length) return unchanged;

  const byGroup = new Map();
  for (const basin of basins) {
    if (!basin.fixtureGroupId) continue;
    if (!byGroup.has(basin.fixtureGroupId)) {
      byGroup.set(basin.fixtureGroupId, []);
    }
    byGroup.get(basin.fixtureGroupId).push(basin);
  }
  if (!byGroup.size) return unchanged;

  const priorById = new Map(
    (priorObjects || []).filter((o) => o?.id).map((o) => [o.id, o])
  );
  let next = objects;
  const created = [];
  const removedIds = [];
  const touched = [];

  for (const [groupId, groupBasins] of byGroup) {
    const cabinet = sinkBody(next, groupBasins[0], groupId);
    const wall = hostWall({ ...room, objects: next }, cabinet);
    const projector = wall ? makeWallProjector(wall) : null;
    // A mirror and a light have nothing to hang on without a wall — a basin
    // on a free-standing island leg gets neither, and that is the answer, not
    // a degraded one.
    if (!cabinet || !wall || !projector) continue;

    const moved = repositionGroup({
      objects: next,
      groupId,
      basins: groupBasins,
      priorById,
      cabinet,
      wall,
      projector,
      room: { ...room, objects: next },
    });
    next = moved.objects;
    removedIds.push(...moved.removedIds);

    const topped = topUpGroup({
      objects: next,
      groupId,
      basins: groupBasins,
      cabinet,
      wall,
      projector,
      room: { ...room, objects: next },
      roomId: roomId || room?.id,
      addedBy,
      overheadLightModel,
    });
    next = topped.objects;
    created.push(...topped.created);
    if (moved.removedIds.length || topped.created.length) touched.push(groupId);
  }

  if (next === objects) return unchanged;
  // The record, so the Group Editor opens populated and the solver keeps the
  // mirror over the basin on the next drag. Through the RUN's own glue
  // (`attachKitchenSinkLinks`, which `materializeRun` already calls for every
  // sink group it solves) — one emitter per group, and the mirror/light rules
  // live in it precisely so this pass needs no second one.
  if (touched.length) {
    next = attachKitchenSinkLinksForGroups(next, touched);
  }
  // A culled accessory leaves its auto-links on the anchor, pointing at an
  // object the room no longer has.
  if (removedIds.length) next = pruneDanglingGroupLinks(next);
  return { objects: next, created, removedIds };
}

module.exports = { planVanityRunAccessories, runBasins };
